import Lottie from "lottie-web";
import { useState, useEffect, Dispatch, SetStateAction, useRef } from "react";
import loadingAnimation from "../../asset/lotties/loading_animation.json";
import { useNavigate } from "react-router-dom";
import { useDebounce } from "react-use";

import {
  RIF,
  _,
  useCurrentProjectState,
  useCurrentWorkspaceState,
  RequestResult,
  checkIsStaffUser,
  loadSessionStorage,
} from "../../lib";
import {
  useSelectTheme,
  useCreateDispatchActions,
  useSelectMethod,
  useSelectTempTaskData,
  useSelectProfile,
} from "../../store";
import {
  Button,
  ProjectFinishSetupPop,
  ProjectSavePop,
  ProjectDiscardPop,
  SlideCheckbox,
} from "..";
import {
  CheckDefaultIcon,
  CheckSuccessIcon,
  CloudIcon,
  NoInternetIcon,
  WarningIcon,
} from "../../asset/image";
import { ProjectStatus, ProjectAuditTrailAction, IProject, ProjectType } from "../../shared/db";
import { TaskType } from "../../shared/db";
import {
  getProjectContentDiffWithProject,
  ProjectContent,
  TaskStateType,
  TempTask,
} from "../../model";
import { isEmpty } from "lodash";

export interface ProjectSetupBarProps {
  projectContent: ProjectContent | null;
  finishedRequiredField: boolean;
  isEditingLiveProject: boolean;
  setDisplayWarningDevicePop: Dispatch<
    SetStateAction<Record<"stopwatch_movesense_stream" | "stopwatch_garmin_stream", boolean>>
  >;
  displayFinishPopup: boolean;
  setDisplayFinishPopup: Dispatch<SetStateAction<boolean>>;
}

export const ProjectSetupBar = (props: ProjectSetupBarProps) => {
  const { pad, color, fontSize } = useSelectTheme();

  const {
    projectContent,
    finishedRequiredField,
    isEditingLiveProject,
    setDisplayWarningDevicePop,
    displayFinishPopup,
    setDisplayFinishPopup,
    ...rest
  } = props;

  const {
    doREQUEST_PROJECT_UPDATE,
    doREQUEST_PROJECT_STATUS_UPDATE,
    doREQUEST_METHOD_UPDATE,
    doREQUEST_METHOD_TASK_INDEX_ORDER_UPDATE,
    doREQUEST_PROJECT_AUDIT_TRAIL_CREATE,
  } = useCreateDispatchActions();

  const [requestResultOfStatusUpdate, setRequestResultOfStatusUpdate] =
    useState<RequestResult | null>(null);

  const navigate = useNavigate();
  const { projectId, project } = useCurrentProjectState();
  const methodState = useSelectMethod();
  const methodId = methodState?.id as string;
  const dexcomIntegrationId = methodState?.dexcomIntegrationId;
  const profile = useSelectProfile();
  const loadSession = loadSessionStorage();
  const isStaffUser = checkIsStaffUser(loadSession?.identity);

  const { workspace: currentWorkspace } = useCurrentWorkspaceState();
  const dexcomIntegrationList = currentWorkspace?.dexcomIntegrationList ?? [];
  const dexcomIntegration = dexcomIntegrationId
    ? _.find(dexcomIntegrationList, { id: dexcomIntegrationId })
    : undefined;

  const dexcomAwaitingApproval =
    !!dexcomIntegrationId &&
    !!dexcomIntegration &&
    !dexcomIntegration?.dexcomApp?.dexcomIntegrationId;

  const tempTaskData = useSelectTempTaskData();
  const movesenseDeviceEnable = methodState.movesenseDeviceEnable;
  const tempMovesenseDeviceEnable = tempTaskData.movesenseDeviceEnable;
  // todo check_StopwatchMovesenseStream
  const movesenseTasks: TaskStateType[] = _.filter(methodState?.taskList, {
    type: TaskType.StopwatchMovesenseStream || TaskType.StopwatchMovesenseLogData,
  });
  const tempMovesenseTasks: TempTask[] = _.filter(tempTaskData?.taskList, {
    type: TaskType.StopwatchMovesenseStream || TaskType.StopwatchMovesenseLogData,
  });
  const hasMovesenseTasks: boolean = !!movesenseTasks.length || !!tempMovesenseTasks.length;

  const garminStreamEnable = methodState.garminStreamEnable;
  const tempGarminStreamEnable = tempTaskData.garminStreamEnable;
  const garminStreamTasks: TaskStateType[] = _.filter(methodState?.taskList, {
    type: TaskType.StopwatchGarminStream,
  });
  const tempGarminStreamTasks: TempTask[] = _.filter(tempTaskData?.taskList, {
    type: TaskType.StopwatchGarminStream,
  });
  const hasGarminStreamTasks: boolean =
    !!garminStreamTasks.length || !!tempGarminStreamTasks.length;

  const [displayUpdatePopup, setDisplayUpdatePopup] = useState(false);
  const [displayDiscardPopup, setDisplayDiscardPopup] = useState(false);
  const [isTemplateProject, setIsTemplateProject] = useState(project?.type === "template");

  useEffect(() => {
    if (!requestResultOfStatusUpdate?.success) return;
    if (projectId) {
      if (!isStaffUser) {
        doREQUEST_PROJECT_AUDIT_TRAIL_CREATE({
          payload: {
            projectId: projectId,
            action: ProjectAuditTrailAction.Update,
            before: { status: ProjectStatus.Draft },
            after: { status: ProjectStatus.Live },
          },
        });
      }
      return navigate(`/dashboard/${projectId}`);
    }
  }, [requestResultOfStatusUpdate]);

  /* ------------------ auto save ------------------ */

  const getProjectUpdatePayload = (
    projectContent: ProjectContent,
  ): Partial<IProject> & { projectId?: string } => {
    return {
      projectId: projectId || undefined,
      name: projectContent.name || undefined,
      investigator: projectContent.investigator || undefined,
      organization: projectContent.organization || undefined,
      description: projectContent.description || undefined,
      participantInstructions: projectContent.instructionValue || undefined,
      contactDescription: projectContent.contactValue || undefined,
    };
  };

  const autoUpdateProject = (projectContent: ProjectContent) => {
    const isEmptyUpdates = isEmpty(getProjectContentDiffWithProject({ projectContent, project }));
    if (!isEmptyUpdates) {
      doREQUEST_PROJECT_UPDATE({
        payload: getProjectUpdatePayload(projectContent),
        disableLoadingState: true,
      });
    }
  };

  const [isOnline, setIsOnline] = useState(true);
  const [isAutoSaving, setIsAutoSaving] = useState(false);
  const [savedLessThanMinute, setSavedLessThanMinute] = useState(true);

  const animRef = useRef<HTMLDivElement>(null);

  window.ononline = () => setIsOnline(true);
  window.onoffline = () => setIsOnline(false);

  const [projectContentDebouncedSecond, setProjectContentDebouncedSecond] =
    useState<ProjectContent | null>(null);
  const [projectContentDebouncedMinute, setProjectContentDebouncedMinute] =
    useState<ProjectContent | null>(null);

  useDebounce(() => setProjectContentDebouncedSecond(projectContent), 1000, [projectContent]);
  useDebounce(() => setProjectContentDebouncedMinute(projectContent), 60000, [projectContent]);

  useEffect(() => {
    if (projectContent && isOnline) setIsAutoSaving(true);
  }, [projectContent]);

  useEffect(() => {
    if (!isOnline) return;
    if (!isEditingLiveProject && projectContentDebouncedSecond) {
      autoUpdateProject(projectContentDebouncedSecond);
    }
    setIsAutoSaving(false);
    setSavedLessThanMinute(true);
  }, [projectContentDebouncedSecond]);

  useEffect(() => {
    if (!isOnline) return;
    setSavedLessThanMinute(false);
  }, [projectContentDebouncedMinute]);

  useEffect(() => {
    if (!animRef.current) return;
    const anim = Lottie.loadAnimation({
      container: animRef.current,
      renderer: "svg",
      loop: true,
      autoplay: true,
      animationData: loadingAnimation,
    });
    return () => anim.stop();
  }, [isAutoSaving]);

  const updateTaskIndexOrder = () => {
    const taskIdList = methodState.taskList
      ?.filter((task) => {
        return task.type !== TaskType.GarminDevice;
      })
      .sort((a, b) => {
        if (a.eventTracker && !b.eventTracker) return -1;
        if (!a.eventTracker && b.eventTracker) return 1;
        return 0;
      })
      .map(({ id }) => id);
    if (!taskIdList?.length) return;

    doREQUEST_METHOD_TASK_INDEX_ORDER_UPDATE({
      payload: {
        methodId,
        taskIdList,
      },
    });
  };

  const handleSave = () => {
    if (!projectContent) return;

    updateTaskIndexOrder();

    doREQUEST_PROJECT_UPDATE({
      payload: {
        ...getProjectUpdatePayload(projectContent),
        type: isTemplateProject ? ProjectType.Template : ProjectType.User,
      },
    });

    if (isEditingLiveProject && tempTaskData.dexcomIntegrationId) {
      doREQUEST_METHOD_UPDATE({
        payload: {
          methodId,
          dexcomIntegrationId: tempTaskData.dexcomIntegrationId,
        },
      });
    }
  };

  const handleFinish = () => {
    doREQUEST_PROJECT_STATUS_UPDATE({
      setRequestResult: setRequestResultOfStatusUpdate,
      payload: {
        projectId: projectId,
        status: ProjectStatus.Live,
      },
    });
  };

  const handleClickFinish = () => {
    handleSave();
    if (project?.status === ProjectStatus.Draft) {
      if (movesenseDeviceEnable && !hasMovesenseTasks)
        // todo check_stopwatch_movesense_stream
        return setDisplayWarningDevicePop((prev) => ({
          ...prev,
          stopwatch_movesense_stream: true,
        }));
      if (garminStreamEnable && !hasGarminStreamTasks)
        return setDisplayWarningDevicePop((prev) => ({ ...prev, stopwatch_garmin_stream: true }));
      return setDisplayFinishPopup(true);
    }
    if (project?.status === ProjectStatus.Live) {
      if (tempMovesenseDeviceEnable && !hasMovesenseTasks)
        // todo check_stopwatch_movesense_stream
        return setDisplayWarningDevicePop((prev) => ({
          ...prev,
          stopwatch_movesense_stream: true,
        }));
      if (tempGarminStreamEnable && !hasGarminStreamTasks)
        return setDisplayWarningDevicePop((prev) => ({ ...prev, stopwatch_garmin_stream: true }));
      return setDisplayUpdatePopup(true);
    }
  };

  const handleDiscard = () => {
    navigate(`/project`);
  };

  return (
    <div
      {...rest}
      css={{
        width: "100vw",
        height: "60px",
        boxShadow: "0px 2px 10px rgba(0, 0, 0, 0.13)",
        padding: `0 ${pad.xl}`,
        display: isEditingLiveProject ? "none" : "flex",
        justifyContent: "space-between",
        alignItems: "center",
        backgroundColor: color.white,
        position: "fixed",
        bottom: "0",
        left: "0",
      }}
    >
      {/* FIXME: interrupted, project status is not updated */}
      {RIF(
        displayFinishPopup,
        <ProjectFinishSetupPop closeAction={setDisplayFinishPopup} doAction={handleFinish} />,
      )}

      {RIF(
        displayUpdatePopup,
        <ProjectSavePop closeAction={setDisplayUpdatePopup} doAction={handleFinish} />,
      )}

      {RIF(
        displayDiscardPopup,
        <ProjectDiscardPop closeAction={setDisplayDiscardPopup} doAction={handleDiscard} />,
      )}

      <div>
        {RIF(
          isOnline,
          <>
            {RIF(
              isAutoSaving,
              <div css={{ display: "flex", alignItems: "center", marginLeft: "-12px" }}>
                <div
                  ref={animRef}
                  css={{
                    width: "45px",
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "center",
                  }}
                />
                <span css={{ color: color.grey_400, marginLeft: "-8px" }}>Saving...</span>
              </div>,
            )}
            {RIF(
              !isAutoSaving,
              <div css={{ display: "flex", alignItems: "center" }}>
                <img src={CloudIcon} width="20" alt="" aria-hidden />
                <span css={{ margin: "0 8px" }}>Saved</span>
                <span css={{ color: color.grey_400 }}>
                  Last edit was {savedLessThanMinute ? "seconds" : "minutes"} ago
                </span>
              </div>,
            )}
          </>,
        )}
        {RIF(
          !isOnline,
          <div css={{ display: "flex", alignItems: "center" }}>
            <img src={NoInternetIcon} width="20" />
            <span css={{ margin: "0 8px" }}>No Internet Connection</span>
          </div>,
        )}
      </div>
      {/* <div css={{
        fontWeight: fontWeight.bold,
        display: 'flex',
        alignItems: 'center'}}>
        <ButtonReverse onClick={handleSave}
          btnPadding='large'
          css={{marginRight: '30px'}}>
          Save and Edit Next Time
        </ButtonReverse>
        <div onClick={() => setDisplayDiscardPopup(true)}
          css={{color: color.warning, cursor: 'pointer'}}>Discard</div>
      </div> */}

      <div
        css={{
          display: "flex",
          alignItems: "center",
        }}
      >
        {RIF(
          profile?.email === "admin@labfront.com",
          <div
            css={{
              marginRight: "20px",
              display: "flex",
              alignItems: "center",
            }}
          >
            <p
              css={{
                color: color.textIcon.light,
                fontSize: fontSize.h6,
                marginRight: "10px",
              }}
            >
              Template Project
            </p>
            <SlideCheckbox
              {...{
                value: isTemplateProject,
                onChange: (e) => setIsTemplateProject(e),
              }}
            />
          </div>,
        )}
        {RIF(
          !finishedRequiredField,
          <div css={{ display: "flex", alignItems: "center", marginRight: "25px" }}>
            <img src={CheckDefaultIcon} width="14" alt="" aria-hidden />
            <span css={{ color: color.grey_400, marginLeft: "6px" }}>
              Please complete all required fields
            </span>
          </div>,
        )}
        {RIF(
          finishedRequiredField && dexcomAwaitingApproval,
          <div css={{ display: "flex", alignItems: "center", marginRight: "25px" }}>
            <img src={WarningIcon} width="14" />
            <span css={{ color: color.warning, marginLeft: "6px" }}>
              Awaiting for Dexcom's Approval
            </span>
          </div>,
        )}
        {RIF(
          finishedRequiredField && !dexcomAwaitingApproval,
          <div css={{ display: "flex", alignItems: "center", marginRight: "25px" }}>
            <img src={CheckSuccessIcon} width="14" alt="" aria-hidden />
            <span css={{ color: color.grey_400, marginLeft: "6px" }}>
              Great! You have completed all required fields!
            </span>
          </div>,
        )}
        <Button
          disabled={!finishedRequiredField || dexcomAwaitingApproval}
          onClick={handleClickFinish}
          css={{ weight: "110px" }}
          type="button"
        >
          Finish Setup
        </Button>
      </div>
    </div>
  );
};
